import React from 'react';
import { X, Sparkles } from 'lucide-react';

const TOUR_KEY = 'immipro_active_tour';
const TOUR_TTL_MS = 60_000; // 60s max after click

/**
 * GuidedTourOverlay — mounted once in AppLayout. When another component (the
 * onboarding checklist) writes `{ testid, tooltip, expiresAt }` into
 * localStorage under TOUR_KEY, this overlay finds the target element by its
 * `data-testid` on the current page, draws an animated highlight ring around
 * it and displays the tooltip nearby. Auto-dismisses on click anywhere, when
 * the target scrolls out of view, or after 15s of being displayed.
 */
export default function GuidedTourOverlay() {
  const [tour, setTour] = React.useState(null);
  const [rect, setRect] = React.useState(null);

  // Read localStorage on mount + on every route change (poll cheaply)
  React.useEffect(() => {
    const readTour = () => {
      try {
        const raw = localStorage.getItem(TOUR_KEY);
        if (!raw) { setTour(null); return; }
        const t = JSON.parse(raw);
        if (!t?.testid || (t.expiresAt && Date.now() > t.expiresAt)) {
          localStorage.removeItem(TOUR_KEY);
          setTour(null); return;
        }
        setTour(t);
      } catch { setTour(null); }
    };
    readTour();
    // Watch for our own writes + navigations
    const onStorage = (e) => { if (e.key === TOUR_KEY) readTour(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('immipro-tour-updated', readTour);
    const interval = setInterval(readTour, 500);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('immipro-tour-updated', readTour);
      clearInterval(interval);
    };
  }, []);

  // Once we have a tour, poll for the target element (page may still be loading)
  React.useEffect(() => {
    if (!tour) { setRect(null); return; }
    let stopped = false;
    let attempts = 0;
    const dismissTimer = setTimeout(() => dismiss(), 15_000);

    const findTarget = () => {
      if (stopped) return;
      const el = document.querySelector(`[data-testid="${tour.testid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const r = el.getBoundingClientRect();
        setRect({
          top: r.top + window.scrollY,
          left: r.left + window.scrollX,
          width: r.width,
          height: r.height,
        });
        return;
      }
      attempts += 1;
      if (attempts < 30) setTimeout(findTarget, 300);
      else dismiss();
    };
    findTarget();

    const onReposition = () => {
      const el = document.querySelector(`[data-testid="${tour.testid}"]`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top + window.scrollY,
        left: r.left + window.scrollX,
        width: r.width, height: r.height,
      });
    };
    window.addEventListener('scroll', onReposition, { passive: true });
    window.addEventListener('resize', onReposition);
    return () => {
      stopped = true;
      clearTimeout(dismissTimer);
      window.removeEventListener('scroll', onReposition);
      window.removeEventListener('resize', onReposition);
    };
  }, [tour?.testid]);

  const dismiss = () => {
    localStorage.removeItem(TOUR_KEY);
    setTour(null);
    setRect(null);
  };

  if (!tour || !rect) return null;

  // Tooltip position: below the target if room, else above
  const padding = 12;
  const tooltipWidth = 320;
  const targetCenter = rect.left + rect.width / 2;
  const tooltipLeft = Math.max(16, Math.min(window.innerWidth - tooltipWidth - 16, targetCenter - tooltipWidth / 2));
  const belowRoom = window.innerHeight - (rect.top + rect.height - window.scrollY);
  const showBelow = belowRoom > 180;
  const tooltipTop = showBelow ? rect.top + rect.height + padding : rect.top - 170;

  return (
    <div className="pointer-events-none fixed inset-0 z-[9999]" data-testid="guided-tour-overlay">
      {/* Animated highlight ring */}
      <div
        data-testid="guided-tour-ring"
        className="pointer-events-none absolute rounded-lg border-2 border-[#2ECC9A]"
        style={{
          top: rect.top - window.scrollY - 6,
          left: rect.left - window.scrollX - 6,
          width: rect.width + 12,
          height: rect.height + 12,
          boxShadow: '0 0 0 9999px rgba(15,23,42,0.28), 0 0 20px 4px rgba(46,204,154,0.5)',
          animation: 'guided-tour-pulse 1.6s ease-in-out infinite',
          transition: 'top 0.25s, left 0.25s, width 0.25s, height 0.25s',
        }}
      />

      {/* Tooltip */}
      <div
        data-testid="guided-tour-tooltip"
        className="pointer-events-auto absolute w-80 rounded-lg border border-[#2ECC9A]/30 bg-white p-4 shadow-xl"
        style={{ top: tooltipTop, left: tooltipLeft }}
      >
        <div className="flex items-start gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2ECC9A]/15">
            <Sparkles className="h-3.5 w-3.5 text-[#2ECC9A]" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#2ECC9A]">Guided tour</div>
            <div className="mt-1 text-sm leading-snug text-[#1A3A5C]">{tour.tooltip}</div>
            <button
              type="button"
              onClick={dismiss}
              data-testid="guided-tour-got-it"
              className="mt-2 inline-flex items-center gap-1 rounded-md bg-[#1A3A5C] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#1A3A5C]/90"
            >
              Got it
            </button>
          </div>
          <button type="button" onClick={dismiss} data-testid="guided-tour-close"
                  className="ml-1 text-slate-400 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes guided-tour-pulse {
          0%, 100% { box-shadow: 0 0 0 9999px rgba(15,23,42,0.28), 0 0 16px 4px rgba(46,204,154,0.4); }
          50%      { box-shadow: 0 0 0 9999px rgba(15,23,42,0.28), 0 0 28px 8px rgba(46,204,154,0.75); }
        }
      `}</style>
    </div>
  );
}

/** Public helper — write a tour target to localStorage; overlay picks it up. */
export function startTour({ testid, tooltip, from }) {
  const payload = {
    testid, tooltip, from: from || '',
    startedAt: Date.now(),
    expiresAt: Date.now() + TOUR_TTL_MS,
  };
  localStorage.setItem(TOUR_KEY, JSON.stringify(payload));
  window.dispatchEvent(new Event('immipro-tour-updated'));
}
